// A part's 3D model as the plan and the section views project it — the model
// the 3D view builds (three/scene3d.ts) for a part whose body (partLook.ts) is
// projected: a stored model (`model3d`), or a built body's — a duct fan, a steam
// conveyor, a steam cube, a hot tub, an essence doser — generated from its
// figures as one (builtModels.ts `bodyModel`), both through the one builder
// (shared/partSolids.ts; docs/claude/tilt.md, *Parts with a 3D model*). Each
// solid is a set of convex pieces, so in a view it is its pieces' hulls (or
// slices), drawn without the seams between them (`solidPieces`). Pure and
// DOM-free (test/partModel.test.mjs).

import type {
  PartModel3d,
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  PRODUCT_COLOR,
  solidGroups,
  solidPieces3,
} from '../shared/partSolids';
import { partHeight } from './heights';
import { bodyModel } from './builtModels';
import {
  isProjected,
  solidLook,
} from './partLook';
import {
  type Body,
  bodyOf,
  bodyPoint,
  type CutPlane,
  hull,
  type Vec3,
} from './tilt';

/** One solid of a part's model in its own frame (x across its width and y across
 *  its depth from its middle, z up from its underside): its convex pieces, each
 *  its corners, and its colour. Water (and glass) is drawn see-through. */
export interface ModelSolid { pieces: Vec3[][]; color: string; water?: true }

/** A solid as the plan draws it: its pieces (`solidPieces`) and colour. */
export interface ModelOutline { pieces: ModelPiece[]; color: string; water?: true }

/** A solid in a section view, in the view's frame (x along the cut, y up): the
 *  pieces of its slice where the plane cuts it and of its outline beyond. */
export interface ModelCut { cut: ModelPiece[]; seen: ModelPiece[]; color: string; water?: true }

/** Facets round a revolved body. */
const ROUND = 32;

const signedArea = (r: readonly Vec2[]): number => {
  let s = 0;
  for (let i = 0; i < r.length; i++) { const p = r[i], q = r[(i + 1) % r.length]; s += p.x * q.y - q.x * p.y; }
  return s / 2;
};

/** A part's model as solids to project: one per colour role (`solidGroups`), the
 *  `body` in the copy's own colour, its glass and its water see-through. */
function modelSolids(inst: ProductInstance, m: PartModel3d): ModelSolid[] {
  return solidGroups(m, inst.color, PRODUCT_COLOR).map((g) => ({
    pieces: g.solids.flatMap((s) => solidPieces3(s, ROUND)), color: g.colour, ...(g.glass || g.water ? { water: true as const } : {}),
  }));
}

const MODELS = new Map<string, ModelSolid[] | null>();

/** The part's 3D model, or null for a part whose body is not projected
 *  (partLook.ts `isProjected`: it keeps its own drawing on the plan, and a box
 *  in a section). */
export function partModel(inst: ProductInstance): ModelSolid[] | null {
  return memo(MODELS, modelKey(inst), () => {
    const { body } = solidLook(inst);
    return isProjected(body) ? modelSolids(inst, bodyModel(inst, body)) : null;
  });
}

/** The part's model seen from above standing upright on the floor (`modelPlan`),
 *  or none: what the plan and its DXF draw for a heater block or a part shown
 *  as its model (partLook.ts, `heaterBlock` / `model`). */
export function uprightModelPlan(inst: ProductInstance): ModelOutline[] {
  return modelPlan(inst, bodyOf(inst, 0, { w: inst.width, d: inst.depth, h: partHeight(inst) }));
}

/** The value `key` holds in `cache`, made by `make` when it holds none — past
 *  `size` entries the least lately used dropped. A drawing's every modelled part
 *  in every section view is drawn on each repaint: the caches hold that many. */
function memo<T>(cache: Map<string, T>, key: string, make: () => T, size = 256): T {
  if (cache.has(key)) {
    const v = cache.get(key) as T;
    cache.delete(key);
    cache.set(key, v);
    return v;
  }
  const v = make();
  cache.set(key, v);
  if (cache.size > size) cache.delete(cache.keys().next().value as string);
  return v;
}

/** A convex piece of a solid as a view draws it: its ring (counter-clockwise)
 *  and, side by side (from each point to the next), whether the side is hidden —
 *  where another piece of its solid lies against or over it. A solid's pieces
 *  filled, their shown sides stroked, draw the solid without its seams. */
export interface ModelPiece { pts: Vec2[]; hidden: boolean[] }

/** The pieces of a solid in a view: each convex ring with the stretches of its
 *  sides another ring covers hidden (a side split where that begins or ends).
 *  Rings with no area are left out. */
export function solidPieces(rings: readonly Vec2[][]): ModelPiece[] {
  const ccw = rings.filter((r) => r.length >= 3 && Math.abs(signedArea(r)) > 1e-3).map((r) => (signedArea(r) > 0 ? r : r.slice().reverse()));
  const boxes = ccw.map((r) => {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const q of r) { x0 = Math.min(x0, q.x); y0 = Math.min(y0, q.y); x1 = Math.max(x1, q.x); y1 = Math.max(y1, q.y); }
    return [x0, y0, x1, y1];
  });
  return ccw.map((r, i) => {
    const pts: Vec2[] = [], hidden: boolean[] = [];
    for (let k = 0; k < r.length; k++) {
      const a = r[k], b = r[(k + 1) % r.length];
      const dx = b.x - a.x, dy = b.y - a.y, l = Math.hypot(dx, dy);
      if (l < 1e-9) continue;
      // The side moved a hair outward: where another ring holds it, the side
      // has that ring against or over it.
      const ox = (dy / l) * SEAM, oy = (-dx / l) * SEAM;
      const A = { x: a.x + ox, y: a.y + oy }, B = { x: b.x + ox, y: b.y + oy };
      const covered: Array<[number, number]> = [];
      for (let j = 0; j < ccw.length; j++) {
        if (j === i) continue;
        const [x0, y0, x1, y1] = boxes[j];
        if (Math.max(A.x, B.x) < x0 - SEAM || Math.min(A.x, B.x) > x1 + SEAM || Math.max(A.y, B.y) < y0 - SEAM || Math.min(A.y, B.y) > y1 + SEAM) continue;
        const span = insideSpan(A, B, ccw[j]);
        if (span) covered.push(span);
      }
      covered.sort((u, v) => u[0] - v[0]);
      let at = 0;
      const put = (t: number, hide: boolean): void => { pts.push({ x: a.x + dx * t, y: a.y + dy * t }); hidden.push(hide); };
      for (const [t0, t1] of covered) {
        if (t1 <= at) continue;
        if (t0 > at + 1e-9) { put(at, false); at = t0; }
        put(at, true);
        at = t1;
      }
      if (at < 1 - 1e-9) put(at, false);
    }
    return { pts, hidden };
  });
}

/** How far outward a side is moved to ask what lies against it (mm). */
const SEAM = 0.05;

/** Where along a → b (0…1) the segment lies inside the convex counter-clockwise
 *  ring, or null (Cyrus–Beck). */
function insideSpan(a: Vec2, b: Vec2, ring: readonly Vec2[]): [number, number] | null {
  let t0 = 0, t1 = 1;
  for (let k = 0; k < ring.length; k++) {
    const p = ring[k], q = ring[(k + 1) % ring.length];
    const nx = -(q.y - p.y), ny = q.x - p.x;
    const num = nx * (a.x - p.x) + ny * (a.y - p.y), den = nx * (b.x - a.x) + ny * (b.y - a.y);
    if (Math.abs(den) < 1e-12) { if (num < -1e-9) return null; continue; }
    const t = -num / den;
    if (den > 0) t0 = Math.max(t0, t); else t1 = Math.min(t1, t);
    if (t0 > t1 - 1e-9) return null;
  }
  return [t0, t1];
}

/** A convex ring cut down to x0 ≤ x ≤ x1. */
function clipSpan(ring: readonly Vec2[], x0: number, x1: number): Vec2[] {
  const clip = (pts: readonly Vec2[], keep: (p: Vec2) => number): Vec2[] => {
    const out: Vec2[] = [];
    pts.forEach((p, i) => {
      const q = pts[(i + 1) % pts.length], kp = keep(p), kq = keep(q);
      if (kp >= 0) out.push(p);
      if ((kp >= 0) !== (kq >= 0)) { const f = kp / (kp - kq); out.push({ x: p.x + (q.x - p.x) * f, y: p.y + (q.y - p.y) * f }); }
    });
    return out;
  };
  return clip(clip(ring, (p) => p.x - x0), (p) => x1 - p.x);
}

const bodyKey = (b: Body): string => JSON.stringify([b.x, b.y, b.base, b.size, b.m]);
const modelKey = (inst: ProductInstance): string => JSON.stringify([inst.category, inst.fanModel, inst.model3d, inst.throughWall, inst.hotTubModel, inst.doserModel, inst.width, inst.depth, inst.height, inst.color, (inst.nodes?.length ?? 0) >= 2]);

const PLANS = new Map<string, ModelOutline[]>();

/** The model of a part standing as `b` (tilted or not) seen from above: each
 *  solid's pieces, the solids in paint order — the one reaching lowest first (as
 *  a section's, the farthest: a fan's open end turned up shows over its body,
 *  the one turned down lies under it). */
export function modelPlan(inst: ProductInstance, b: Body): ModelOutline[] {
  const model = partModel(inst);
  if (!model) return [];
  return memo(PLANS, `${modelKey(inst)}|${bodyKey(b)}`, () =>
    model
      .map((s) => {
        let low = Infinity;
        const rings = s.pieces.map((pc) => hull(pc.map((v) => { const w = bodyPoint(b, v); low = Math.min(low, w.z); return { x: w.x, y: w.y }; })));
        return { view: { pieces: solidPieces(rings), color: s.color, ...(s.water ? { water: true as const } : {}) }, low };
      })
      .sort((p, q) => p.low - q.low)
      .map((v) => v.view));
}

/** A model seen from above (`modelPlan`) as lines, for a drawing with no fills
 *  (the plan DXF): each solid's shown sides, less where a solid painted after it
 *  — water aside — lies over them, as runs of joined segments. */
export function modelPlanLines(views: readonly ModelOutline[]): Array<{ pts: Vec2[]; color: string }> {
  const out: Array<{ pts: Vec2[]; color: string }> = [];
  views.forEach((v, k) => {
    const over = views.slice(k + 1).filter((w) => !w.water).flatMap((w) => w.pieces.map((pc) => pc.pts));
    for (const pc of v.pieces) {
      let run: Vec2[] = [];
      const flush = (): void => { if (run.length >= 2) out.push({ pts: run, color: v.color }); run = []; };
      pc.pts.forEach((a, i) => {
        if (pc.hidden[i]) { flush(); return; }
        const b = pc.pts[(i + 1) % pc.pts.length];
        const spans = over.map((r) => insideSpan(a, b, r)).filter((x): x is [number, number] => !!x).sort((x, y) => x[0] - y[0]);
        let at = 0;
        const at01 = (t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
        const shown = (t0: number, t1: number): void => {
          if (t1 - t0 < 1e-9) return;
          if (t0 > 1e-9) flush();
          if (!run.length) run.push(at01(t0));
          run.push(at01(t1));
        };
        for (const [t0, t1] of spans) {
          if (t1 <= at) continue;
          shown(at, t0);
          flush();
          at = Math.max(at, t1);
        }
        shown(at, 1);
        if (at > 1 - 1e-9) flush();
      });
      flush();
    }
  });
  return out;
}

const SECTIONS = new Map<string, ModelCut[]>();

/** The model of a part standing as `b` in a section view along `p` (x along the
 *  cut from `p.a`, y up), within the view's length `span`: each solid's slice
 *  where the plane cuts it and its outline beyond the plane, the solids in paint
 *  order — the one reaching farthest first. */
export function modelInSection(inst: ProductInstance, b: Body, p: CutPlane, span: [number, number] = [-Infinity, Infinity]): ModelCut[] {
  const model = partModel(inst);
  if (!model) return [];
  return memo(SECTIONS, `${modelKey(inst)}|${bodyKey(b)}|${JSON.stringify([p, span])}`, () =>
    solidsInSection(model.map((s) => ({ ...s, pieces: s.pieces.map((pc) => pc.map((v) => bodyPoint(b, v))) })), p, span)
      .map(({ cut, seen, solid }) => ({ cut, seen, color: solid.color, ...(solid.water ? { water: true as const } : {}) })), 1024);
}

/** Solids given as convex pieces in the world (x, y plan, z up) in a view: each
 *  one's slices where the plane cuts it and its outline beyond, the far ones
 *  first, each solid's pieces with the seams between them hidden. */
export function solidsInSection<S extends { pieces: Vec3[][] }>(solids: readonly S[], p: CutPlane, span: [number, number] = [-Infinity, Infinity]): Array<{ solid: S; cut: ModelPiece[]; seen: ModelPiece[] }> {
  const depth = (q: Vec3): number => (q.x - p.a.x) * p.look.x + (q.y - p.a.y) * p.look.y;
  const th = (q: Vec3): Vec2 => ({ x: (q.x - p.a.x) * p.u.x + (q.y - p.a.y) * p.u.y, y: q.z });
  const fit = (r: Vec2[]): Vec2[] => (isFinite(span[0]) || isFinite(span[1]) ? clipSpan(r, span[0], span[1]) : r);
  return solids
    .map((s) => {
      const cuts: Vec2[][] = [], seens: Vec2[][] = [];
      let far = -Infinity;
      for (const w of s.pieces) {
        const ds = w.map(depth);
        const on: Vec2[] = [];
        for (let i = 0; i < w.length; i++) {
          if (Math.abs(ds[i]) <= 1e-9) { on.push(th(w[i])); continue; }
          for (let j = i + 1; j < w.length; j++) {
            if ((ds[i] < 0 && ds[j] > 1e-9) || (ds[i] > 0 && ds[j] < -1e-9)) {
              const k = ds[i] / (ds[i] - ds[j]);
              on.push(th({ x: w[i].x + (w[j].x - w[i].x) * k, y: w[i].y + (w[j].y - w[i].y) * k, z: w[i].z + (w[j].z - w[i].z) * k }));
            }
          }
        }
        if (on.length >= 3) cuts.push(fit(hull(on)));
        const beyond = w.filter((_, i) => ds[i] > 1e-9);
        if (beyond.length) {
          seens.push(fit(hull([...beyond.map(th), ...on])));
          for (const q of beyond) far = Math.max(far, depth(q));
        }
      }
      return { v: { solid: s, cut: solidPieces(cuts), seen: solidPieces(seens) }, far };
    })
    .sort((x, y) => y.far - x.far)
    .map((x) => x.v);
}
